import moment from "moment";
import { IconType } from "react-icons";
import {
  FiActivity,
  FiBookOpen,
  FiMoon,
  FiStar,
  FiSunrise,
  FiUsers,
  FiWind,
} from "react-icons/fi";

import {
  AdventCampaign,
  AdventDoor,
  AdventDoorCategory,
  AdventDoorType,
} from "apps/user/learner/api/types";

const DOOR_DATE_FORMAT = "YYYY-MM-DD";

export enum DoorState {
  Locked = "locked",
  Available = "available",
  Opened = "opened",
}

export enum ContentTarget {
  Recording = "recording",
  Session = "session",
  Article = "article",
}

export interface ContentDestination {
  target: ContentTarget;
  /** The raw id with its `re_` / `ls_` / `ar_` prefix stripped. */
  id: string;
}

const CONTENT_PREFIXES: Record<string, ContentTarget> = {
  re_: ContentTarget.Recording,
  ls_: ContentTarget.Session,
  ar_: ContentTarget.Article,
};

/** The learner's local calendar date, as the API formats door dates. */
export const localToday = (): string => moment().format(DOOR_DATE_FORMAT);

/**
 * Calendar-day comparison on the Y-M-D parts: the door's date is a plain
 * date, so no instant or offset maths is involved. This is the display
 * answer only; the server judges an open against the learner's profile
 * timezone and answers DOOR_LOCKED if the two disagree.
 */
export const isDoorUnlocked = (door: AdventDoor, today: string): boolean =>
  moment(door.date, DOOR_DATE_FORMAT).isSameOrBefore(
    moment(today, DOOR_DATE_FORMAT),
    "day"
  );

export const isDoorForToday = (door: AdventDoor, today: string): boolean =>
  door.date === today;

export const doorState = (door: AdventDoor, today: string): DoorState => {
  if (door.isOpened) return DoorState.Opened;
  if (isDoorUnlocked(door, today)) return DoorState.Available;
  return DoorState.Locked;
};

/** A plain `YYYY-MM-DD` as "Monday 14 December" in the learner's language. */
export const formatDoorDate = (date: string, language: string): string => {
  const [year, month, day] = date.split("-").map(Number);
  return new Intl.DateTimeFormat(language, {
    weekday: "long",
    day: "numeric",
    month: "long",
    timeZone: "UTC",
  }).format(new Date(Date.UTC(year, month - 1, day)));
};

/** Whole calendar days from today until `date`; negative once it has passed. */
export const daysUntil = (date: string, today: string): number =>
  moment(date, DOOR_DATE_FORMAT).diff(moment(today, DOOR_DATE_FORMAT), "days");

/** Today's door when there is one for the calendar date. */
export const todaysDoor = (
  doors: AdventDoor[],
  today: string
): AdventDoor | undefined => doors.find((door) => isDoorForToday(door, today));

export const doorByDay = (
  doors: AdventDoor[],
  day: number
): AdventDoor | undefined => doors.find((door) => door.dayNumber === day);

/**
 * FNV-1a, 32-bit, over the slug's UTF-16 code units (identical to its UTF-8
 * bytes: campaign slugs are ASCII). Mobile derives the same seed.
 */
const fnv1a32 = (text: string): number => {
  let hash = 0x811c9dc5;
  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
};

/** mulberry32: a 32-bit PRNG yielding floats in [0, 1). */
const mulberry32 = (seed: number): (() => number) => {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) | 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

/**
 * The order the doors are laid out in: a real advent calendar scatters its
 * numbers rather than counting 1 to 24. The arrangement is fixed per
 * campaign and identical for every learner on every surface, so it is
 * derived from the slug alone:
 *
 *   hash    = FNV-1a 32-bit of the slug
 *   PRNG    = mulberry32(hash)
 *   shuffle = Fisher–Yates over the day numbers 1..count ascending,
 *             i from count-1 down to 1, j = floor(random() * (i + 1))
 *
 * Mobile runs the same algorithm; the unit test pins the output for a slug
 * so the two can be cross-checked.
 */
export const shuffledDayOrder = (slug: string, count: number): number[] => {
  const random = mulberry32(fnv1a32(slug));
  const days = Array.from({ length: count }, (_, index) => index + 1);
  for (let i = days.length - 1; i > 0; i -= 1) {
    const j = Math.floor(random() * (i + 1));
    [days[i], days[j]] = [days[j], days[i]];
  }
  return days;
};

/** The campaign's doors in their scattered calendar order. */
export const scatterDoors = (
  doors: AdventDoor[],
  slug: string
): AdventDoor[] => {
  const byDay = new Map(doors.map((door) => [door.dayNumber, door]));
  return shuffledDayOrder(slug, doors.length).map((day) => {
    const door = byDay.get(day);
    if (!door) throw new Error(`Campaign "${slug}" has no door for day ${day}`);
    return door;
  });
};

/** Doors whose day has come but which the learner has not opened yet. */
export const unopenedUnlockedCount = (
  doors: AdventDoor[],
  today: string
): number =>
  doors.filter((door) => !door.isOpened && isDoorUnlocked(door, today)).length;

/**
 * Whole days until the first door, or 0 once the run is under way. The API
 * serves an upcoming campaign a week ahead, so the page counts down to it.
 */
export const daysToStart = (campaign: AdventCampaign, today: string): number =>
  Math.max(0, daysUntil(campaign.startDate, today));

export const isBeforeStart = (
  campaign: AdventCampaign,
  today: string
): boolean => daysToStart(campaign, today) > 0;

/** The last door's day has passed: the run is over, whatever is still shut. */
export const isAfterRun = (campaign: AdventCampaign, today: string): boolean =>
  campaign.doors.every(
    (door) => isDoorUnlocked(door, today) && !isDoorForToday(door, today)
  );

/** Opened doors in day order, the recap door itself excluded. */
export const openedDoors = (doors: AdventDoor[]): AdventDoor[] =>
  doors
    .filter((door) => door.isOpened && door.doorType !== "RECAP")
    .sort((a, b) => a.dayNumber - b.dayNumber);

export const parseContentDestination = (
  contentId: string
): ContentDestination => {
  const prefix = contentId.slice(0, 3);
  const target = CONTENT_PREFIXES[prefix];
  if (!target) {
    throw new Error(`Unrecognised door content id "${contentId}"`);
  }
  return { target, id: contentId.slice(3) };
};

export const contentCtaKey = (target: ContentTarget): string => {
  const keys: Record<ContentTarget, string> = {
    [ContentTarget.Recording]: "decemberDoors:cta.playRecording",
    [ContentTarget.Session]: "decemberDoors:cta.joinSession",
    [ContentTarget.Article]: "decemberDoors:cta.readArticle",
  };
  return keys[target];
};

export const CATEGORY_ICONS: Record<AdventDoorCategory, IconType> = {
  QUICK_RESET: FiWind,
  MIND: FiMoon,
  MOVEMENT: FiActivity,
  LIFESTYLE: FiSunrise,
  SPECIAL: FiStar,
};

const DOOR_TYPE_ICONS: Partial<Record<AdventDoorType, IconType | null>> = {
  SOCIAL: FiUsers,
  RECAP: FiBookOpen,
  NOTHING: null,
};

/** The icon the door's sheet leads with; `null` for a door that has none. */
export const doorIcon = (door: AdventDoor): IconType | null => {
  const override = DOOR_TYPE_ICONS[door.doorType];
  return override === undefined ? CATEGORY_ICONS[door.category] : override;
};

export const categoryLabelKey = (category: AdventDoorCategory): string => {
  const keys: Record<AdventDoorCategory, string> = {
    QUICK_RESET: "decemberDoors:category.quickReset",
    MIND: "decemberDoors:category.mind",
    MOVEMENT: "decemberDoors:category.movement",
    LIFESTYLE: "decemberDoors:category.lifestyle",
    SPECIAL: "decemberDoors:category.special",
  };
  return keys[category];
};

/** Mixpanel carries the server's lowercase enum values, identical to mobile. */
export const analyticsValue = (value: string): string => value.toLowerCase();

const TOUR_KEY_PREFIX = "december-doors:tour:";
const BLOOM_KEY = "december-doors:first-open";

/** The welcome tour shows once per device for each campaign. */
export const hasSeenTour = (slug: string): boolean =>
  localStorage.getItem(TOUR_KEY_PREFIX + slug) !== null;

export const markTourSeen = (slug: string): void =>
  localStorage.setItem(TOUR_KEY_PREFIX + slug, "1");

/** True the first time a door opens on this device, and never again. */
export const claimFirstOpen = (): boolean => {
  if (localStorage.getItem(BLOOM_KEY) !== null) return false;
  localStorage.setItem(BLOOM_KEY, "1");
  return true;
};

export const prefersReducedMotion = (): boolean =>
  Boolean(window.matchMedia?.("(prefers-reduced-motion: reduce)").matches);
